import React, { useState, useRef } from 'react';
import { Table, Column } from './Table';
import { Modal } from './Modal';
import { SAIFileResponse, saiFilesApi } from '../services/api';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';

registerAllModules();

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController,
  ScatterController
);

interface MetricData {
  r2_score?: number;
  mean_absolute_error?: number;
  mean_squared_error?: number;
  max_absolute_error?: number;
  pred_values?: number[];
  actual_values?: number[];
}

interface TrainedModel {
  id: number;
  name: string;
  data_points: number;
  key_metrics: string;
  remarks: string;
  algorithm: string;
  status: string;
  train_date: string;
  test_metric?: MetricData;
  train_metric?: MetricData;
  additional_params?: any;
}

interface Parameter {
  id: string;
  name: string;
  label: string;
  min: number;
  max: number;
  default: number;
  unit: string;
  type: string;
}

interface SAIDetailsProps {
  selectedFile: SAIFileResponse;
  onBack: () => void;
  onNext: () => void;
  onFileUpdate?: (updatedFile: SAIFileResponse) => void;
}

export const SAIDetails: React.FC<SAIDetailsProps> = ({ selectedFile: selectedFileProp, onBack, onNext, onFileUpdate }) => {
  const [previewImage, setPreviewImage] = useState<string | null>(
    selectedFileProp.image ?? null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SAIFileResponse>(selectedFileProp);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [saveDataError, setSaveDataError] = useState<string | null>(null);
  const [hotTableData, setHotTableData] = useState<Record<string, any>[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hotRef = useRef<any>(null);
  const [selectedModelForMetrics, setSelectedModelForMetrics] = useState<TrainedModel | null>(null);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  // Get parameters from the selected SAI file
  const [parameters, setParameters] = useState<Parameter[]>(
    (selectedFile.parameters || []).map(param => ({
      id: param.id?.toString() || '',
      name: param.name || '',
      label: param.label || '',
      min: param.min || 0,
      max: param.max || 0,
      default: param.default || 0,
      unit: param.unit || '',
      type: param.type || 'input',
    }))
  );

  // Store original parameters for comparison
  const originalParametersRef = useRef<Parameter[]>(
    (selectedFile.parameters || []).map(param => ({
      id: param.id?.toString() || '',
      name: param.name || '',
      label: param.label || '',
      min: param.min || 0,
      max: param.max || 0,
      default: param.default || 0,
      unit: param.unit || '',
      type: param.type || 'input',
    }))
  );

  const trainedModels: TrainedModel[] = selectedFile.trained_models || [];

  const tableColumns: Column[] = [
    { key: 'name', header: 'Model Name' },
    { key: 'data_points', header: 'Data Points', render: (item) => `${item.data_points}` },
    {
      key: 'key_metrics',
      header: 'Key Metrics',
      render: (item: TrainedModel) => {
        const html = item.key_metrics;
        if (html == null || html === '') return null;
        return <span dangerouslySetInnerHTML={{ __html: html }} />;
      },
    },
    { key: 'remarks', header: 'Remarks' },
    { key: 'training_date', header: 'Training Date', render: (item) => new Date(item.train_date).toLocaleString() },
    {
      key: 'actions',
      header: 'Actions',
      render: (item) => (
        <button 
          className="table-action-btn"
          onClick={() => {
            setSelectedModelForMetrics(item);
            setIsMetricsModalOpen(true);
          }}
          title="View Training Metrics"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M2.5 10C2.5 10 5.5 4.16667 10 4.16667C14.5 4.16667 17.5 10 17.5 10C17.5 10 14.5 15.8333 10 15.8333C5.5 15.8333 2.5 10 2.5 10Z" stroke="#103097" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#103097" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )
    },
  ];

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        setUploadingImage(true);
        setImageUploadError(null);

        // Show preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewImage(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Upload image to backend
        const formData = new FormData();
        formData.append('image', file);

        const updatedFile = await saiFilesApi.update(selectedFile.id, formData, true);

        // Update the selectedFile state with the response
        setSelectedFile(updatedFile);

        // Update preview image from the response
        if (updatedFile.image) {
          setPreviewImage(updatedFile.image);
        }

        // Notify parent component of the update
        if (onFileUpdate) {
          onFileUpdate(updatedFile);
        }

        console.log('Image uploaded successfully');
      } catch (error) {
        console.error('Error uploading image:', error);
        setImageUploadError('Failed to upload image. Please try again.');
        // Revert to previous image if available, otherwise show default
        setPreviewImage(selectedFile.image ?? null);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } finally {
        setUploadingImage(false);
      }
    } else {
      setImageUploadError('Please select a valid image file.');
    }
  };

  const handleOpenEditModal = () => {
    // Reset parameters from the current selectedFile state
    const currentParameters = (selectedFile.parameters || []).map(param => ({
      id: param.id?.toString() || '',
      name: param.name || '',
      label: param.label || '',
      min: param.min || 0,
      max: param.max || 0,
      default: param.default || 0,
      unit: param.unit || '',
      type: param.type || 'input',
    }));

    setParameters(currentParameters);
    originalParametersRef.current = currentParameters;
    setSaveDataError(null);
    setHotTableData([
      currentParameters.reduce<Record<string, string>>((row, param) => {
        row[param.id] = param.label;
        return row;
      }, { datasource: 'data source' }),
      currentParameters.reduce<Record<string, string>>((row, param) => {
        row[param.id] = param.unit;
        return row;
      }, { datasource: '' }),
      currentParameters.reduce<Record<string, string>>((row, param) => {
        row[param.id] = param.type;
        return row;
      }, { datasource: '' }),
    ]);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSaveDataError(null);
    // Reset parameters to original values
    setParameters([...originalParametersRef.current]);
  };

  const handleSaveEditData = async () => {
    try {
      setSaveDataError(null);
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      const tableData = hot.getData();

      const lastColIndex = hot.countCols() - 1;
      const paramColCount = parameters.length;

      // Validate data rows: no missing values in parameter columns (exclude data source column)
      const rowsWithMissingValues: number[] = [];
      for (let rowIdx = 3; rowIdx < tableData.length; rowIdx++) {
        const row = tableData[rowIdx];
        const paramValues = row?.slice(0, paramColCount) ?? [];
        const isAllNull = paramValues.every((val: any) => val === null || val === undefined || val === '');
        if (isAllNull) continue;

        const hasMissing = paramValues.some((val: any) => val === null || val === undefined || val === '');
        if (hasMissing) {
          rowsWithMissingValues.push(rowIdx + 1);
        }
      }
      if (rowsWithMissingValues.length > 0) {
        const rowList = rowsWithMissingValues.join(', ');
        setSaveDataError(`Missing values in row(s): ${rowList}. Please fill in all parameter cells or leave the entire row empty.`);
        return;
      }

      // Update labels (row 0, exclude last column)
      const update: Record<string, any> = {};
      parameters.forEach((param, colIndex) => {
        if (colIndex === paramColCount - 1) return;
        const newLabel = tableData[0]?.[colIndex];
        if (newLabel !== undefined && newLabel !== param.label) {
          if (!update[param.id]) update[param.id] = {};
          update[param.id].label = newLabel;
        }
      });

      if (Object.keys(update).length > 0) {
        const payload = { parameters: { update } };
        const updatedFile = await saiFilesApi.update(selectedFile.id, payload);
        setSelectedFile(updatedFile);
        originalParametersRef.current = [...parameters];
        
        // Notify parent component of the update
        if (onFileUpdate) {
          onFileUpdate(updatedFile);
        }
      }

      // Build add_data payload from rows starting at index 3
      const dataRows: number[][] = [];
      const sources: string[] = [];

      for (let rowIdx = 3; rowIdx < tableData.length; rowIdx++) {
        const row = tableData[rowIdx];
        const paramValues = row.slice(0, paramColCount);
        const isAllNull = paramValues.every((val: any) => val === null || val === undefined || val === '');
        if (isAllNull) continue;

        dataRows.push(paramValues.map((val: any) => (val !== null && val !== undefined && val !== '' ? Number(val) : 0)));
        sources.push(row[lastColIndex] ?? '');
      }

      if (dataRows.length > 0) {
        const addDataPayload = {
          param_short_name: parameters.map(p => p.name),
          param_long_name: parameters.map(p => p.label),
          param_default_value: parameters.map(p => p.default),
          data: dataRows,
          quality: dataRows.map(() => 'good'),
          source: sources,
        };

        const updatedFile = await saiFilesApi.addData(selectedFile.id, addDataPayload);
        setSelectedFile(updatedFile);
        
        // Notify parent component of the update
        if (onFileUpdate) {
          onFileUpdate(updatedFile);
        }
      }

      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error saving edit data:', error);
    }
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Simerics AI Model</h1>
      </div>

      <div className="sai-preview-section">
        <div className="sai-preview-container">
          <div className="sai-preview-placeholder">
            {previewImage ? (
              <img src={previewImage} alt="SAI Preview" className="sai-preview-image" />
            ) : (
              <span className="sai-preview-placeholder-text">No image. Please upload one.</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            className="btn btn-secondary upload-image-btn"
            onClick={handleUploadImage}
            disabled={uploadingImage}
          >
            {uploadingImage ? (
              <>Uploading...</>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M14.1667 6.66667L10 2.5M10 2.5L5.83333 6.66667M10 2.5V12.5" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Upload New Image
              </>
            )}
          </button>
          {imageUploadError && (
            <p style={{ color: '#dc3545', marginTop: '0.5rem', fontSize: '0.875rem' }}>
              {imageUploadError}
            </p>
          )}
        </div>
      </div>

      <div className="sai-details-content">
        <div className="sai-info-section">
          <div className="sai-info-cards">
            <div className="sai-info-card">
              <div className="sai-info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9H21M3 15H21M12 3V21M7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3Z" stroke="#103097" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div className="sai-info-content">
                <h3>Data Points</h3>
                <p className="sai-info-value">{selectedFile.datapoint_count} data point{selectedFile.datapoint_count !== 1 ? 's' : ''}</p>
              </div>
              <button className="sai-edit-btn" onClick={handleOpenEditModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M2.39662 15.0964C2.43491 14.7518 2.45405 14.5795 2.50618 14.4185C2.55243 14.2756 2.61778 14.1396 2.70045 14.0143C2.79363 13.873 2.91621 13.7504 3.16136 13.5052L14.1666 2.5C15.0871 1.57953 16.5795 1.57953 17.4999 2.5C18.4204 3.42048 18.4204 4.91286 17.4999 5.83334L6.49469 16.8386C6.24954 17.0837 6.12696 17.2063 5.98566 17.2995C5.86029 17.3821 5.72433 17.4475 5.58146 17.4937C5.42042 17.5459 5.24813 17.565 4.90356 17.6033L2.08325 17.9167L2.39662 15.0964Z" stroke="#103097" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Edit
              </button>
            </div>

            <div className="sai-info-card">
              <div className="sai-info-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M13 7L11.8845 4.76892C11.5634 4.1268 11.4029 3.80573 11.1634 3.57116C10.9516 3.36373 10.6963 3.20597 10.4161 3.10931C10.0992 3 9.74021 3 9.02229 3H5.2C4.0799 3 3.51984 3 3.09202 3.21799C2.71569 3.40973 2.40973 3.71569 2.21799 4.09202C2 4.51984 2 5.0799 2 6.2V7M2 7H17.2C18.8802 7 19.7202 7 20.362 7.32698C20.9265 7.6146 21.3854 8.07354 21.673 8.63803C22 9.27976 22 10.1198 22 11.8V16.2C22 17.8802 22 18.7202 21.673 19.362C21.3854 19.9265 20.9265 20.3854 20.362 20.673C19.7202 21 18.8802 21 17.2 21H6.8C5.11984 21 4.27976 21 3.63803 20.673C3.07354 20.3854 2.6146 19.9265 2.32698 19.362C2 18.7202 2 17.8802 2 16.2V7ZM9 14L11 16L15.5 11.5" stroke="#103097" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              </div>
              <div className="sai-info-content">
                <h3>Trained Models</h3>
                <p className="sai-info-value">{trainedModels.length} record{trainedModels.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          <div className="trained-models-section">
            <h2>Trained Models</h2>
            <Table
              items={trainedModels}
              columns={tableColumns}
              scrollable
            />
          </div>
        </div>
      </div>

      <button className="btn btn-primary continue-btn-full" onClick={onNext}>
        Continue
        <span className="arrow-right">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="#273444" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="black" stroke-opacity="0.2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        </span>
      </button>

      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Data Points"
        onSave={handleSaveEditData}
      >
        {saveDataError && (
          <p className="edit-data-error" role="alert">
            {saveDataError}
          </p>
        )}
        <HotTable
          ref={hotRef}
          themeName="ht-theme-main"
          data={hotTableData}
          afterChange={() => {
            const hot = hotRef.current?.hotInstance;
            if (!hot) return;
            const tableData = hot.getData() as (string | number | null)[][];
            const paramColCount = parameters.length;
            const newData: Record<string, any>[] = tableData.map((rowArr) => {
              const obj: Record<string, any> = {};
              parameters.forEach((p, i) => {
                obj[p.id] = rowArr[i] ?? '';
              });
              obj.datasource = rowArr[paramColCount] ?? '';
              return obj;
            });
            setHotTableData(newData);
          }}
          colHeaders={[
            ...parameters.map(param => (param.name.toLowerCase())),
            ' ',
          ]}
          rowHeaders={false}
          height="auto"
          stretchH="all"
          autoWrapRow={true}
          autoWrapCol={true}
          minSpareRows={1}
          cells={(row, col) => {
            const isMetadataRow = row <= 2;
            const isDataSourceCol = col === parameters.length;

            if (isMetadataRow) {
              return {
                readOnly: row === 1 || row === 2 || isDataSourceCol,
                className: 'hot-metadata-row',
              };
            }
            if (isDataSourceCol) {
              return { className: 'hot-datasource-col' };
            }
            return {};
          }}
          licenseKey="non-commercial-and-evaluation"
        />
      </Modal>

      <Modal
        isOpen={isMetricsModalOpen}
        onClose={() => {
          setIsMetricsModalOpen(false);
          setSelectedModelForMetrics(null);
        }}
        title="Training Metrics"
      >
        {selectedModelForMetrics && (() => {
          // Debug logging
          console.log('Selected Model for Metrics:', selectedModelForMetrics);
          console.log('Test actual values:', selectedModelForMetrics.test_metric?.actual_values);
          console.log('Test pred values:', selectedModelForMetrics.test_metric?.pred_values);
          console.log('Train actual values:', selectedModelForMetrics.train_metric?.actual_values);
          console.log('Train pred values:', selectedModelForMetrics.train_metric?.pred_values);

          // Convert pred_values and actual_values to scatter plot format
          const convertToScatterData = (actual: number[] | undefined, predicted: number[] | undefined) => {
            if (!actual || !predicted || actual.length !== predicted.length) {
              console.log('Scatter data conversion failed:', { actual, predicted });
              return [];
            }
            return actual.map((act, index) => ({
              x: act,
              y: predicted[index],
            }));
          };

          const testScatterData = convertToScatterData(
            selectedModelForMetrics.test_metric?.actual_values,
            selectedModelForMetrics.test_metric?.pred_values
          );

          const trainScatterData = convertToScatterData(
            selectedModelForMetrics.train_metric?.actual_values,
            selectedModelForMetrics.train_metric?.pred_values
          );

          console.log('Test scatter data points:', testScatterData.length);
          console.log('Train scatter data points:', trainScatterData.length);

          // Calculate axis limits based on output parameter's min/max ± 10%
          const getAxisLimits = () => {
            // Find the output parameter from selectedFile
            const outputParam = selectedFile?.parameters?.find(
              param => (param.type || '').toLowerCase() === 'output'
            );

            if (outputParam && outputParam.min != null && outputParam.max != null) {
              const range = outputParam.max - outputParam.min;
              const padding = range * 0.1; // 10% padding
              return {
                min: outputParam.min - padding,
                max: outputParam.max + padding,
              };
            }

            // Fallback to data-based limits if no output parameter found
            const allValues = [
              ...testScatterData.map(point => point.x),
              ...testScatterData.map(point => point.y),
              ...trainScatterData.map(point => point.x),
              ...trainScatterData.map(point => point.y),
            ];
            return {
              min: allValues.length > 0 ? Math.floor(Math.min(...allValues)) : 0,
              max: allValues.length > 0 ? Math.ceil(Math.max(...allValues)) : 1,
            };
          };

          const { min, max } = getAxisLimits();
          console.log('Chart axis limits:', { min, max });

          return (
            <div className="training-results" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Left side - Metrics Table */}
              <div>
                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--gray200)', marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: 'var(--primaryText)' }}>Model Information</h4>
                  <div style={{ display: 'grid', gap: '12px', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--secondary)' }}>Model Name:</span>
                      <span style={{ color: 'var(--primaryText)', fontWeight: '500' }}>{selectedModelForMetrics.name}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--secondary)' }}>Algorithm:</span>
                      <span style={{ color: 'var(--primaryText)', fontWeight: '500' }}>{selectedModelForMetrics.algorithm}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--secondary)' }}>Training Date:</span>
                      <span style={{ color: 'var(--primaryText)', fontWeight: '500' }}>
                        {new Date(selectedModelForMetrics.train_date).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--secondary)' }}>Training Samples:</span>
                      <span style={{ color: 'var(--primaryText)', fontWeight: '500' }}>
                        {selectedModelForMetrics.data_points} records
                      </span>
                    </div>
                    {selectedModelForMetrics.remarks && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--secondary)' }}>Remarks:</span>
                        <span style={{ color: 'var(--primaryText)', fontWeight: '500' }}>{selectedModelForMetrics.remarks}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--gray200)' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: 'var(--primaryText)' }}>Performance Metrics</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--gray200)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--secondary)', fontWeight: '600' }}>Metric</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--secondary)', fontWeight: '600' }}>Test</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--secondary)', fontWeight: '600' }}>Train</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid var(--gray100)' }}>
                        <td style={{ padding: '12px 8px', color: 'var(--primaryText)' }}>R² Score</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.test_metric?.r2_score != null 
                            ? selectedModelForMetrics.test_metric?.r2_score.toFixed(4)
                            : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.train_metric?.r2_score != null
                            ? selectedModelForMetrics.train_metric?.r2_score.toFixed(4)
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--gray100)' }}>
                        <td style={{ padding: '12px 8px', color: 'var(--primaryText)' }}>Mean Absolute Error</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.test_metric?.mean_absolute_error != null
                            ? selectedModelForMetrics.test_metric?.mean_absolute_error.toFixed(4)
                            : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.train_metric?.mean_absolute_error != null
                            ? selectedModelForMetrics.train_metric?.mean_absolute_error.toFixed(4)
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid var(--gray100)' }}>
                        <td style={{ padding: '12px 8px', color: 'var(--primaryText)' }}>Mean Squared Error</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.test_metric?.mean_squared_error != null
                            ? selectedModelForMetrics.test_metric?.mean_squared_error.toFixed(4)
                            : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.train_metric?.mean_squared_error != null
                            ? selectedModelForMetrics.train_metric?.mean_squared_error.toFixed(4)
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px 8px', color: 'var(--primaryText)' }}>Max Absolute Error</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.test_metric?.max_absolute_error != null
                            ? selectedModelForMetrics.test_metric?.max_absolute_error.toFixed(4)
                            : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--primaryText)', fontWeight: '500' }}>
                          {selectedModelForMetrics.train_metric?.max_absolute_error != null
                            ? selectedModelForMetrics.train_metric?.max_absolute_error.toFixed(4)
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right side - Chart */}
              <div>
                {(testScatterData.length > 0 || trainScatterData.length > 0) ? (
                  <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--gray200)', height: '100%' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: 'var(--primaryText)' }}>Predicted vs Actual</h4>
                    <div style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                      <Scatter
                        data={{
                          datasets: [
                            {
                              label: 'Test data',
                              data: testScatterData,
                              backgroundColor: '#7C8DC6',
                              borderColor: '#344FA7',
                              pointRadius: 5,
                              pointHoverRadius: 8,
                            },
                            {
                              label: 'Train data',
                              data: trainScatterData,
                              backgroundColor: '#89D889',
                              borderColor: '#29B829',
                              pointRadius: 5,
                              pointHoverRadius: 8,
                            },
                            {
                              type: 'line',
                              label: 'Perfect prediction',
                              data: [{ x: min, y: min }, { x: max, y: max }],
                              borderColor: 'rgba(0, 0, 0, 0.8)',
                              borderWidth: 2,
                              pointRadius: 0,
                              fill: false,
                              showLine: true,
                              hidden: false,
                            } as any,
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          aspectRatio: 1,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                              align: 'start',
                              labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 15,
                                color: '#000000',
                                font: {
                                  size: 12,
                                  weight: 'normal',
                                  family: 'Lexend',
                                },
                                boxWidth: 10,
                                boxHeight: 10,
                                filter: function(item) {
                                  return item.text !== 'Perfect prediction';
                                }
                              },
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const x = context.parsed?.x ?? 0;
                                  const y = context.parsed?.y ?? 0;
                                  return `Actual: ${x.toFixed(4)}, Predicted: ${y.toFixed(4)}`;
                                }
                              }
                            }
                          },
                          scales: {
                            x: {
                              type: 'linear',
                              position: 'bottom',
                              title: {
                                display: true,
                                text: 'Actual',
                                color: '#000000',
                                font: {
                                  size: 14,
                                  weight: 'normal',
                                  family: 'Lexend',
                                },
                              },
                              min: min,
                              max: max,
                            },
                            y: {
                              type: 'linear',
                              position: 'left',
                              title: {
                                display: true,
                                text: 'Predicted',
                                color: '#000000',
                                font: {
                                  size: 14,
                                  weight: 'normal',
                                  family: 'Lexend',
                                },
                              },
                              min: min,
                              max: max,
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--gray200)', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center', color: 'var(--secondary)' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>No chart data available</p>
                      <p style={{ margin: '0', fontSize: '0.875rem' }}>
                        Predicted and actual values are not available for this model.
                      </p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: 'var(--gray400)' }}>
                        Check browser console for debug information.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

